import * as React from "react";
import { useContext, useState, useRef, useCallback, useEffect } from "react";
import styled, { css, keyframes } from "styled-components";
import delay from "lodash/delay";
import { useFormikContext } from "formik";
import Popover from "components/common/Popover";
import Icon from "components/common/Icon";
import StringUtils from "util/StringUtils";
import DocumentationLink from "components/support/DocumentationLink";
import DocsHelper from "util/DocsHelper";
import QueryValidationActions from "views/actions/QueryValidationActions";
import FormWarningsContext from "contexts/FormWarningsContext";
const Container = styled.div`
  margin-left: 5px;
  width: 25px;
  min-height: 34px;
  display: flex;
  align-items: center;
  justify-content: center;
`;
const ExplanationTrigger = styled.button(
  ({ $clickable }) => css`
    padding: 0;
    background: transparent;
    border: 0;
    display: flex;
    align-items: center;
    cursor: ${$clickable ? "pointer" : "default"};
  `
);
export const DocumentationIcon = styled(Icon)`
  margin-left: 5px;
`;
const Title = styled.div`
  display: flex;
  justify-content: space-between;
`;
export const Explanation = styled.div`
  display: flex;
  justify-content: space-between;

  &:not(:last-child) {
    margin-bottom: 6px;
  }
`;
const shakeAnimation = keyframes`
  10%,
  90% {
    transform: translate3d(-1px, 0, 0);
  }

  20%,
  80% {
    transform: translate3d(2px, 0, 0);
  }

  30%,
  50%,
  70% {
    transform: translate3d(-4px, 0, 0);
  }

  40%,
  60% {
    transform: translate3d(4px, 0, 0);
  }
`;
const StyledPopoverDropdown = styled(Popover.Dropdown)(
  ({ $shaking }) => css`
    animation: ${$shaking ? css`
          ${shakeAnimation} 0.82s cubic-bezier(0.36, 0.07, 0.19, 0.97) both
        ` : "none"};
  `
);
const ErrorIcon = styled(Icon)(
  ({ $status }) => css`
    color: ${({ theme }) => {
    if ($status === "ERROR") return theme.colors.variant.danger;
    if ($status === "INFO") return theme.colors.variant.info;
    return theme.colors.variant.warning;
  }};
    font-size: 22px;
    animation: ${$status === "INFO" ? css`
          ${shakeAnimation} 0.82s cubic-bezier(0.36, 0.07, 0.19, 0.97) both
        ` : "none"};
    animation-iteration-count: 2;
  `
);
const ExplanationTitle = ({ title }) => /* @__PURE__ */ React.createElement(Title, null, title);
const useShakeTemporarily = () => {
  const [shouldShake, setShake] = useState(false);
  const shake = useCallback(() => {
    if (!shouldShake) {
      setShake(true);
      delay(() => setShake(false), 820);
    }
  }, [shouldShake]);
  return [shouldShake, shake];
};
const useTriggerIfErrorsPersist = (trigger) => {
  const [showExplanation, setShowExplanation] = useState(false);
  const toggleShow = useCallback(() => setShowExplanation((prevShow) => !prevShow), []);
  useEffect(() => {
    const unsubscribe = QueryValidationActions.displayValidationErrors.listen(() => {
      if (!showExplanation) {
        toggleShow();
      } else {
        trigger();
      }
    });
    return () => {
      unsubscribe();
    };
  }, [trigger, showExplanation, toggleShow]);
  return [showExplanation, toggleShow];
};
const deduplicateExplanations = (explanations) => {
  if (explanations === null || explanations.length === 0) {
    return [];
  }
  const [deduplicated] = explanations.reduce(
    ([prevExplanations, seen], cur) => {
      if (cur.errorType === "UNKNOWN_FIELD") {
        if (cur.relatedProperty !== void 0 && !seen.includes(cur.relatedProperty)) {
          return [
            [...prevExplanations, cur],
            [...seen, cur.relatedProperty]
          ];
        }
        return [prevExplanations, seen];
      }
      return [[...prevExplanations, cur], seen];
    },
    [[], []]
  );
  return deduplicated;
};
const QueryValidation = ({ validationExplanations = [] }) => {
  const [shakingPopover, shake] = useShakeTemporarily();
  const [showExplanation, toggleShow] = useTriggerIfErrorsPersist(shake);
  const explanationTriggerRef = useRef(void 0);
  const {
    errors: { queryString: queryStringErrors }
  } = useFormikContext();
  const { warnings } = useContext(FormWarningsContext);
  const validationState = queryStringErrors != null ? queryStringErrors : warnings == null ? void 0 : warnings.queryString;
  const { status, explanations = [] } = validationState != null ? validationState : { explanations: [] };
  const deduplicatedExplanations = [...deduplicateExplanations(explanations)];
  const hasExplanations = validationState && (validationState == null ? void 0 : validationState.status) !== "OK";
  const isInfo = validationState && validationState.status === "INFO";
  const validationTitle = () => {
    if (!validationState) return "";
    switch (validationState.status) {
      case "WARNING":
        return "warning";
      case "INFO":
        return "information";
      default:
        return "error explanation";
    }
  };
  return /* @__PURE__ */ React.createElement(Popover, { opened: hasExplanations && showExplanation, position: "bottom", width: 500, withArrow: true }, /* @__PURE__ */ React.createElement(Popover.Target, null, /* @__PURE__ */ React.createElement(Container, { ref: explanationTriggerRef }, hasExplanations ? /* @__PURE__ */ React.createElement(
    ExplanationTrigger,
    {
      title: `Toggle validation ${validationTitle()}`,
      onClick: toggleShow,
      $clickable: true,
      tabIndex: 0,
      type: "button"
    },
    /* @__PURE__ */ React.createElement(ErrorIcon, { $status: status, name: isInfo ? "database" : "error" })
  ) : /* @__PURE__ */ React.createElement(
    DocumentationLink,
    {
      page: DocsHelper.PAGES.SEARCH_QUERY_LANGUAGE,
      title: "Search query syntax documentation",
      text: /* @__PURE__ */ React.createElement(Icon, { name: "lightbulb_circle" })
    }
  ))), hasExplanations && showExplanation && /* @__PURE__ */ React.createElement(
    StyledPopoverDropdown,
    {
      id: "query-validation-error-explanation",
      title: /* @__PURE__ */ React.createElement(ExplanationTitle, { title: StringUtils.capitalizeFirstLetter(status.toLocaleLowerCase()) }),
      $shaking: shakingPopover
    },
    /* @__PURE__ */ React.createElement("div", { role: "alert" }, deduplicatedExplanations.map(({ errorType, errorTitle, errorMessage, id }) => /* @__PURE__ */ React.createElement(Explanation, { key: id }, /* @__PURE__ */ React.createElement("span", null, /* @__PURE__ */ React.createElement("b", null, errorTitle), ": ", errorMessage), errorType && /* @__PURE__ */ React.createElement(
      DocumentationLink,
      {
        page: DocsHelper.PAGES.SEARCH_QUERY_ERRORS,
        title: "Query error documentation",
        text: /* @__PURE__ */ React.createElement(DocumentationIcon, { name: "lightbulb_circle" })
      }
    ))), validationExplanations.map((PlugableExplanation, index) => (
      // eslint-disable-next-line react/no-array-index-key
      /* @__PURE__ */ React.createElement(PlugableExplanation, { validationState, key: index })
    )))
  ));
};
export default QueryValidation;
